import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  Alert, Button, TextField, InputAdornment, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import {
  ArrowBack, Search, PictureAsPdf, Payment, Edit, Delete, Warning
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function Pagos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pagos, setPagos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [openEditar, setOpenEditar] = useState(false);
  const [openEliminar, setOpenEliminar] = useState(false);
  const [pagoEditar, setPagoEditar] = useState(null);
  const [pagoEliminar, setPagoEliminar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargarPagos(); }, []);

  const cargarPagos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/cuentas-pagar/pagos`, { headers: getHeaders() });
      const data = res.data?.data || [];
      setPagos(data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
    } catch (err) { console.error('Error cargando pagos:', err); }
    finally { setLoading(false); }
  };

  const pagosFiltrados = pagos.filter(p => {
    if (!filtro) return true;
    const f = filtro.toLowerCase();
    return (p.proveedor_nombre || '').toLowerCase().includes(f) ||
      (p.proveedor_ruc || '').includes(f) ||
      (p.numero_comprobante || '').includes(f) ||
      (p.referencia || '').toLowerCase().includes(f);
  });

  const formatFecha = (fecha) => fecha ? new Date(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
  const formatMonto = (monto) => `$${parseFloat(monto || 0).toFixed(2)}`;
  const formaPagoLabel = {
    EFECTIVO: { label: 'Efectivo', color: 'success' },
    TRANSFERENCIA: { label: 'Transferencia', color: 'info' },
    CHEQUE: { label: 'Cheque', color: 'warning' },
    TARJETA_CREDITO: { label: 'Tarjeta', color: 'secondary' },
  };

  const abrirEditar = (pago) => { setPagoEditar({ ...pago }); setOpenEditar(true); };

  const guardarEdicion = async () => {
    if (!pagoEditar) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/cuentas-pagar/pagos/${pagoEditar.id}`, {
        monto: parseFloat(pagoEditar.monto),
        fecha: pagoEditar.fecha,
        forma_pago: pagoEditar.forma_pago,
        referencia: pagoEditar.referencia,
        observaciones: pagoEditar.observaciones,
      }, { headers: getHeaders() });
      setMensaje({ tipo: 'success', texto: 'Pago actualizado. Contabilidad ajustada automáticamente.' });
      setOpenEditar(false);
      cargarPagos();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.message || 'Error al actualizar pago' });
    } finally { setSaving(false); }
  };

  const abrirEliminar = (pago) => { setPagoEliminar(pago); setOpenEliminar(true); };

  const confirmarEliminar = async () => {
    if (!pagoEliminar) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/cuentas-pagar/pagos/${pagoEliminar.id}`, { headers: getHeaders() });
      setMensaje({ tipo: 'success', texto: 'Pago eliminado. Asiento contable reversado y saldo de caja/banco restaurado.' });
      setOpenEliminar(false);
      cargarPagos();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.message || 'Error al eliminar pago' });
    } finally { setSaving(false); }
  };

  const descargarPDF = async (pago) => {
    try {
      const res = await axios.get(`${API_URL}/api/cuentas-pagar/pagos/${pago.id}/pdf`,
        { headers: getHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { alert('Error al generar PDF del pago'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/cuentas-pagar')}><ArrowBack /></IconButton>
        <Payment color="primary" />
        <Typography variant="h5" fontWeight={700}>Pagos Registrados</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Payment />}
          onClick={() => navigate('/cuentas-pagar/registrar-pago')}>
          Nuevo Pago
        </Button>
      </Box>

      {mensaje && <Alert severity={mensaje.tipo} sx={{ mb: 2 }} onClose={() => setMensaje(null)}>{mensaje.texto}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField fullWidth size="small" placeholder="Buscar por proveedor, RUC, comprobante..."
          value={filtro} onChange={(e) => setFiltro(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Paper>

      {pagos.length === 0 ? (
        <Alert severity="info">No hay pagos registrados aún.</Alert>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Comprobante</strong></TableCell>
                  <TableCell><strong>Proveedor</strong></TableCell>
                  <TableCell align="right"><strong>Monto</strong></TableCell>
                  <TableCell><strong>Forma Pago</strong></TableCell>
                  <TableCell><strong>Referencia</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagosFiltrados.map(p => {
                  const fp = formaPagoLabel[p.forma_pago] || { label: p.forma_pago, color: 'default' };
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell>{formatFecha(p.fecha)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.numero_comprobante || p.id?.substring(0, 8)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{p.proveedor_nombre || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.proveedor_ruc || ''}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>{formatMonto(p.monto)}</TableCell>
                      <TableCell><Chip label={fp.label} color={fp.color} size="small" /></TableCell>
                      <TableCell>{p.referencia || '—'}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => abrirEditar(p)}><Edit /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => abrirEliminar(p)}><Delete /></IconButton></Tooltip>
                        <Tooltip title="PDF"><IconButton size="small" onClick={() => descargarPDF(p)}><PictureAsPdf /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: '#f9f9f9' }}>
            <Typography variant="body2" color="text.secondary">{pagosFiltrados.length} pago(s)</Typography>
            <Typography variant="body2" fontWeight={700}>Total: {formatMonto(pagosFiltrados.reduce((s, p) => s + parseFloat(p.monto || 0), 0))}</Typography>
          </Box>
        </Paper>
      )}

      {/* Dialog EDITAR */}
      <Dialog open={openEditar} onClose={() => setOpenEditar(false)} maxWidth="sm" fullWidth>
        {pagoEditar && (
          <>
            <DialogTitle>✏️ Editar Pago</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Monto" type="number" value={pagoEditar.monto}
                    onChange={(e) => setPagoEditar({ ...pagoEditar, monto: e.target.value })}
                    inputProps={{ step: '0.01', min: '0.01' }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Fecha" type="date" value={(pagoEditar.fecha || '').substring(0, 10)}
                    onChange={(e) => setPagoEditar({ ...pagoEditar, fecha: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Forma de Pago</InputLabel>
                    <Select value={pagoEditar.forma_pago || 'EFECTIVO'} label="Forma de Pago"
                      onChange={(e) => setPagoEditar({ ...pagoEditar, forma_pago: e.target.value })}>
                      <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                      <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
                      <MenuItem value="CHEQUE">Cheque</MenuItem>
                      <MenuItem value="TARJETA_CREDITO">Tarjeta</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Referencia" value={pagoEditar.referencia || ''}
                    onChange={(e) => setPagoEditar({ ...pagoEditar, referencia: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Observaciones" multiline rows={2} value={pagoEditar.observaciones || ''}
                    onChange={(e) => setPagoEditar({ ...pagoEditar, observaciones: e.target.value })} />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                Si cambia el monto, se generará un asiento contable de ajuste automáticamente.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditar(false)}>Cancelar</Button>
              <Button variant="contained" onClick={guardarEdicion} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog ELIMINAR */}
      <Dialog open={openEliminar} onClose={() => setOpenEliminar(false)} maxWidth="xs" fullWidth>
        {pagoEliminar && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="error" /> Eliminar Pago
            </DialogTitle>
            <DialogContent>
              <Typography gutterBottom>¿Está seguro de eliminar este pago?</Typography>
              <Paper sx={{ p: 2, bgcolor: '#fff3e0', mt: 1 }}>
                <Typography variant="body2"><strong>Proveedor:</strong> {pagoEliminar.proveedor_nombre}</Typography>
                <Typography variant="body2"><strong>Monto:</strong> {formatMonto(pagoEliminar.monto)}</Typography>
                <Typography variant="body2"><strong>Fecha:</strong> {formatFecha(pagoEliminar.fecha)}</Typography>
              </Paper>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Se reversará el asiento contable y se devolverá el saldo a la caja/banco de origen.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEliminar(false)}>Cancelar</Button>
              <Button variant="contained" color="error" onClick={confirmarEliminar} disabled={saving}>
                {saving ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
